import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Collapse,
  Grid2 as Grid,
  Grid2Props,
  IconButton,
  Tab,
  Tabs,
} from "@mui/material";
import { RemoveCircleOutline } from "@mui/icons-material";
import FormLabel from "../../../../components/forms/common/base/FormLabel";
import { useFormHandlerContext } from "../../../../controllers/formController";
import FormSelectField from "../../../../components/forms/fields/basic/SelectField";
import { singleGridItem } from "../../../../components/forms/fields/defaultStyles";
import GroupGridContainer from "../../../../components/common/GroupGridContainer";
import AttributeMetadataItems from "../../../../components/forms/attributeMetadata/AttributeMetadataItems";
import {
  TITLE_EVIDENCE_TYPE_LABELS,
  TitleEvidenceType,
} from "../../../../types/title";
import useEvidenceLogic from "../../../../hooks/logic/evidenceLogic";
import { useInputFullName } from "../../../../components/forms/fields/subForms/NamePrefixContext";
import { useFormContext, useWatch } from "react-hook-form";

const EVIDENCE_TYPE_OPTIONS = Object.entries(TITLE_EVIDENCE_TYPE_LABELS).map(
  ([value, label]) => ({
    value,
    label,
  })
);

const TitleEvidenceSubtype: React.FC<{
  name: string;
  disabled?: boolean;
  evidenceTypeFieldName: string;
}> = ({ disabled = false, evidenceTypeFieldName, name }) => {
  const { externalEvidenceTypeEnum, internalEvidenceTypeEnum } =
    useEvidenceLogic();
  const evidenceSubtypeEnums = useMemo(
    () =>
      ({
        [TitleEvidenceType.INTERNAL_EVIDENCE]: internalEvidenceTypeEnum,
        [TitleEvidenceType.EXTERNAL_EVIDENCE]: externalEvidenceTypeEnum,
        [TitleEvidenceType.MODERN_SCHOLARSHIP]: null,
      }) satisfies Record<TitleEvidenceType, string | null>,
    [internalEvidenceTypeEnum, externalEvidenceTypeEnum]
  );

  const [userEnum, setUserEnum] = useState<string | null>(null);
  const { isProcessing } = useFormHandlerContext();
  const evidenceTypeFieldFullName = useInputFullName(evidenceTypeFieldName);
  const fullName = useInputFullName(name);
  const evidenceTypeValue = useWatch({
    name: evidenceTypeFieldFullName,
  }) as string;
  const { setValue } = useFormContext();

  const isTitleEvidenceType = (v: unknown): v is TitleEvidenceType => {
    return Object.values(TitleEvidenceType).includes(v as TitleEvidenceType);
  };

  useEffect(() => {
    if (!isTitleEvidenceType(evidenceTypeValue)) {
      setValue(fullName, []);
      setUserEnum(null);
      return;
    }

    const nextEnum = evidenceSubtypeEnums[evidenceTypeValue] ?? null;

    if (nextEnum === null) {
      setValue(fullName, []);
      setUserEnum(null);
      return;
    }

    setUserEnum(nextEnum);
  }, [evidenceTypeValue, setValue, fullName, evidenceSubtypeEnums]);

  return userEnum ? (
    <FormSelectField
      name={name}
      label="Evidence Subtype"
      placeholder="Select evidence subtype"
      multiple
      useEnum={userEnum || undefined}
      disabled={disabled || isProcessing}
    />
  ) : null;
};

interface TitleEvidenceItemProps extends Grid2Props {
  evidenceSubTypeFieldName: string;
  evidenceTypeFieldName: string;
  label: string;
  disabled?: boolean;
  remove: () => void;
}

const TitleEvidenceItem: React.FC<TitleEvidenceItemProps> = ({
  evidenceSubTypeFieldName,
  evidenceTypeFieldName,
  label,
  disabled = false,
  remove,
  ...grid
}) => {
  const [metadataVisibility, setMetadataVisibility] = useState<boolean>(false);
  const tabState = metadataVisibility ? "metadata" : "data";
  const handleTabStateChangeInternally = (newState: "data" | "metadata") => {
    setMetadataVisibility(newState === "metadata");
  };
  const { isProcessing } = useFormHandlerContext();

  return (
    <GroupGridContainer spacing={2} {...singleGridItem} {...grid}>
      <Grid {...singleGridItem}>
        <FormLabel
          sx={{ fontSize: "1.2rem" }}
          label={label}
          actions={
            <IconButton
              color="error"
              onClick={() => remove()}
              size="small"
              disabled={disabled || isProcessing}
            >
              <RemoveCircleOutline />
            </IconButton>
          }
        />
      </Grid>
      <Box sx={{ width: "100%" }}>
        <Tabs
          value={tabState}
          onChange={(_event, newValue) =>
            handleTabStateChangeInternally(newValue as "data" | "metadata")
          }
        >
          <Tab label="Data" value="data" />
          <Tab label="Metadata" value="metadata" />
        </Tabs>
        <Box mt={2}>
          <Collapse in={tabState === "data"}>
            <Grid container spacing={2} {...singleGridItem}>
              <FormSelectField
                name={evidenceTypeFieldName}
                label="Evidence Type"
                placeholder="Select evidence type"
                options={EVIDENCE_TYPE_OPTIONS}
                required
                disabled={disabled || isProcessing}
              />
              <TitleEvidenceSubtype
                disabled={disabled || isProcessing}
                evidenceTypeFieldName={evidenceTypeFieldName}
                name={evidenceSubTypeFieldName}
              />
            </Grid>
          </Collapse>
          <Collapse in={tabState === "metadata"}>
            <AttributeMetadataItems
              name="metadata"
              disabled={disabled || isProcessing}
            />
          </Collapse>
        </Box>
      </Box>
    </GroupGridContainer>
  );
};
export default TitleEvidenceItem;
